import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/shell/PasswordField';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { GraduationCap } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../config/api';
import { BRAND_NAME } from '../brand';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'learner',
    tenant_id: ''
  });
  const [tenantSubdomain, setTenantSubdomain] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (value) => {
    setFormData({ ...formData, role: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let tenant_id = null;
      
      // Resolve tenant from subdomain if provided
      if (tenantSubdomain && formData.role !== 'super_admin') {
        try {
          const tenantRes = await axios.get(`${API_URL}/tenants/subdomain/${tenantSubdomain}`);
          tenant_id = tenantRes.data.id;
        } catch (err) {
          toast.error('Organization not found. Please check the subdomain.');
          setLoading(false);
          return;
        }
      }

      const userData = {
        ...formData,
        tenant_id: formData.role === 'super_admin' ? null : tenant_id
      };

      const user = await register(userData);
      toast.success(`Welcome, ${user.first_name}! Your account has been created.`);
      
      // Redirect based on role
      const routes = {
        super_admin: '/super-admin',
        tenant_admin: '/admin',
        instructor: '/instructor',
        parent: '/diary',
        learner: '/learn'
      };
      navigate(routes[user.role] || '/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary-foreground" weight="duotone" />
              </div>
              <span className="font-heading font-bold text-xl">{BRAND_NAME}</span>
            </Link>
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <CardTitle className="font-heading text-2xl">Create your account</CardTitle>
              <CardDescription>Get started with {BRAND_NAME}</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="field">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="field">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div className="field">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="email-input"
                  />
                </div>

                <PasswordField
  id="password"
  label="Password"
  value={formData.password}
  onChange={(v) => handleChange({ target: { name: 'password', value: v } })}
  placeholder="••••••••"
  required
  minLength={6}
  testid="password-input"
  generate
/>

                <div className="field">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={handleRoleChange}>
                    <SelectTrigger data-testid="role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="learner" data-testid="role-learner">
                        Learner
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.role !== 'super_admin' && (
                  <div className="field">
                    <Label htmlFor="tenant">Organization Subdomain</Label>
                    <Input
                      id="tenant"
                      type="text"
                      placeholder="company-name"
                      value={tenantSubdomain}
                      onChange={(e) => setTenantSubdomain(e.target.value)}
                      required={formData.role !== 'super_admin'}
                      data-testid="tenant-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter your organization's subdomain to join
                    </p>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                    Sign in
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-primary/5" />
        <img
          src="https://images.unsplash.com/photo-1768796370577-c6e8b708b980?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzc0MzQ1MzAwfDA&ixlib=rb-4.1.0&q=85"
          alt="Team collaboration"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-4">Join Our Learning Community</h2>
          <p className="text-lg opacity-90">
            Create your account and start your learning journey today.
          </p>
        </div>
      </div>
    </div>
  );
}
